import {
  ArrowLeft,
  BriefcaseBusiness,
  Clock3,
  IdCard,
  Mail,
  UserRound,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { useEffect } from "react";
import { ThreeDots } from "react-loader-spinner";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { EmployeeProfileSection } from "../../components/features/employee-view-directory/components/employee-profile-section.tsx";
import { useEmployeePhotoSource } from "../../components/features/employee-view-directory/hooks/use-employee-photo-source.ts";
import { useEmployeeProfileFields } from "../../components/features/employee-view-directory/hooks/use-employee-profile-fields.ts";
import type { EmployeeDirectoryCardData } from "../../components/features/employee-view-directory/types.ts";
import { Card } from "../../components/shared/card/card.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Avatar, Button } from "../../components/ui/index.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { formatDisplayText } from "../../utils/format-display-text.ts";
import { isInsuranceEligibleLocation } from "../../utils/insurance-eligibility.ts";
import { useUser } from "../../lib/rbac/useUser.ts";
import { formatNavigationLabel, isSQEEmployeeType } from "../../utils/sqe-labs-navigation-access.ts";
import styles from "./employee-view-directory-page.module.css";

function displayDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function displayBirthDateWithoutYear(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

function displayEmployeeType(value: string): string {
  const normalized = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

  if (normalized === "SQELABS") return "SQE Labs";

  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function displayEmployeeSubType(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase() === "FULLTIME"
    ? "Full Time"
    : value;
}

const TECH_ACRONYMS = new Set(["AI", "API", "AWS", "CRM", "GCP", "JS", "ML", "QA", "SQL", "UI", "UX"]);

function displayTechStack(value: string): string {
  if (value === "-") return value;
  if (value.toUpperCase() === "NOTASSIGNED") return "Not Assigned";
  return value
    .replace(/[_-]+/g, " ")
    .split(/(\s+|,\s*|\/)/)
    .map((part) => {
      const normalized = part.trim().toUpperCase();
      if (!normalized || /^\s+$/.test(part) || part === "/" || part.includes(",")) return part;
      if (TECH_ACRONYMS.has(normalized)) return normalized;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join("")
    .replace(/,\s*/g, ", ");
}

type SummaryTile = { label: string; value: string; icon: LucideIcon; tone: string };

export default function EmployeeInformationDetailPage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const user = useUser();
  const cardEmployee = (location.state as { employee?: EmployeeDirectoryCardData } | null)?.employee;
  const photoSource = useEmployeePhotoSource(cardEmployee?.photoPath);
  const cachedProfile = cardEmployee?.profile;
  const resolvedEmployeeId = employeeId ?? (cardEmployee?.id != null ? String(cardEmployee.id) : undefined);
  const fetchedProfile = useEmployeeProfileFields(resolvedEmployeeId);
  const profile = fetchedProfile.profile ?? cachedProfile;
  const loading = !cachedProfile && fetchedProfile.loading;
  const error = fetchedProfile.error;
  const fullName = profile ? [profile.basicDetails.firstName, profile.basicDetails.middleName, profile.basicDetails.lastName].filter((part) => part !== "-").join(" ") : cardEmployee?.employeeName || "Caelius Connect";
  const directoryLabel = formatNavigationLabel("Caelius Connect", user?.empType) ?? "Caelius Connect";

  useEffect(() => {
    setBreadcrumbs([{ label: directoryLabel, path: "/dashboard/yourColleague" }, { label: fullName }]);
    return () => setBreadcrumbs([]);
  }, [directoryLabel, fullName, setBreadcrumbs]);

  if (loading) return <PageShell><div className={styles.detailLoader}><ThreeDots height="56" width="56" color="var(--info)" visible /></div></PageShell>;
  if (!profile) return <PageShell><Button variant="link" startIcon={<ArrowLeft size={16} />} onClick={() => navigate(-1)}>Back to Employees</Button><p className={styles.error}>{error || "Employee information is not available."}</p></PageShell>;

  const eligibility = profile.basicDetails.projectEligibility == null
    ? "-"
    : profile.basicDetails.projectEligibility ? "Eligible" : "Not Eligible";
  const isSQEEmployee = isSQEEmployeeType(profile.workDetails.employeeType)
    || isSQEEmployeeType(cardEmployee?.employeeType);
  const selectedEmail = isSQEEmployee
    ? profile.basicDetails.primaryEmail
    : profile.basicDetails.emailAddress;
  const normalizedEmail = selectedEmail?.trim();
  const displayEmail = normalizedEmail && normalizedEmail !== "-" ? normalizedEmail : "-";
  const isRegularEmployee = profile.workDetails.employeeType.trim().toUpperCase() === "REGULAR";
  const employeeType = displayEmployeeType(profile.workDetails.employeeType);
  const showInsuranceDetails = isInsuranceEligibleLocation(
    profile.workDetails.location,
    profile.workDetails.employeeType,
  );
  const workFields = [
    { label: "Department", value: profile.workDetails.department }, { label: "Date of Joining", value: displayDate(profile.workDetails.dateOfJoining) },
    { label: "Tech Stack", value: displayTechStack(profile.workDetails.techStack) }, { label: "Leave Manager", value: profile.workDetails.leaveManager },
    { label: "Current Location", value: profile.workDetails.currentLocation },
    { label: "Joining Location", value: profile.workDetails.joiningLocation },
    {
      label: "Trailhead",
      value: profile.workDetails.trailhead,
      href: profile.workDetails.trailhead,
      wide: true,
    },
  ];
  const personalFields = [
    { label: "Date of Birth", value: displayBirthDateWithoutYear(profile.personalDetails.dateOfBirth) },
    { label: "Project Eligibility", value: eligibility },
  ];
  const quickInfoFields = [
    { label: "Email", value: displayEmail },
    { label: "Employee Type", value: displayEmployeeType(profile.workDetails.employeeType) },
    { label: "Designation", value: formatDisplayText(profile.workDetails.designation) },
    ...(showInsuranceDetails
      ? [
          { label: "Insurance Number", value: profile.basicDetails.insuranceCardNumber },
          { label: "Valid From", value: displayDate(profile.basicDetails.insuranceValidFrom) },
          { label: "Valid Till", value: displayDate(profile.basicDetails.insuranceValidTill) },
        ]
      : []),
  ];
  const summaryTiles: SummaryTile[] = [
    { label: "Department", value: profile.workDetails.department, icon: BriefcaseBusiness, tone: styles.tileBlue },
    { label: "Designation", value: profile.workDetails.designation, icon: IdCard, tone: styles.tileBlue },
    { label: "Employee Type", value: displayEmployeeType(profile.workDetails.employeeType), icon: UserRound,  tone: styles.tileBlue  },
    ...(isRegularEmployee
      ? [{ label: "Employee Sub Type", value: displayEmployeeSubType(profile.workDetails.employeeSubType), icon: Clock3, tone: styles.tileBlue }]
      : []),
    { label: "Reporting Manager", value: profile.workDetails.reportingManager, icon: UsersRound, tone: styles.tileBlue },
  ];

  return <PageShell className={styles.page}>
    {/* <Button className={styles.backButton} variant="link" startIcon={<ArrowLeft size={16} />} onClick={() => navigate(-1)}>Back to All Employees</Button> */}
    <Card className={styles.profileHeader}>
      <div className={styles.profileIdentity}>
        <Avatar name={fullName} src={photoSource} size="xl" />
        <div className={styles.profileHeaderContent}> 
          <div className={styles.profileNameRow}>
            <p className="heading-sm">{fullName}</p>
         
          </div>
          <p className="sub-heading-md">{formatDisplayText(profile.workDetails.designation)}</p>   
          <div className={styles.profileHeaderMeta}>
            <span><Mail size={14} aria-hidden />{displayEmail}</span>
            {/* <span><CalendarDays size={14} aria-hidden />Joined {displayDate(profile.workDetails.dateOfJoining)}</span> */}
          </div>
        </div>
      </div>
      <div className={styles.summaryTiles}>
        {summaryTiles.map(({ label, value, icon: Icon, tone }) => <div key={label} className={`${styles.summaryTile} ${tone}`}>
          <Icon size={18} aria-hidden />
          <span>{label}</span>
          <strong title={value}>{value}</strong>
        </div>)}
      </div>
    </Card>
    <div className={styles.informationGrid}>
      <EmployeeProfileSection title="Work Details" fields={workFields} />
      <EmployeeProfileSection title="Personal Details" fields={personalFields} columns={1} />
      <EmployeeProfileSection title="Basic Info" fields={quickInfoFields} columns={1} />
    </div>
  </PageShell>;
}
